import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { AlertService } from '../../../../services/alert.service';
import { CompaniesService } from '../../../../services/companies.service';
import { AccountsService } from '../../../../services/accounts.service';
import { TemplatesService } from '../../../../services/templates.service';
import Header from '../../../../components/layout/header.component';
import Asaid from '../../../../components/layout/asaid.component';
import Footer from '../../../../components/layout/footer.component';
import useBgPages from '../../../../hooks/use-bg-pages.hook';
import Select from 'react-select';
import InvalidFeedback from '../../../../components/forms/invalid-feedback.component';
import Input from '../../../../components/forms/input.component';
import TextAreaRCS from '../../../../components/forms/text-area-rcs.component';
import Title from '../../../../components/forms/titles.component';
import Button from '../../../../components/forms/button.component';
import LogData from '../../../../components/logs/log-data.component';
import AndroidMockup from '../../../../components/mockups/android.component';
import Navigate from '../../../../components/cards/header-navigate.component';
import { useSession } from '../../../../hooks/use-session.hook';
import { PROFILES } from '../../../../constants';

interface FormValues {
  _id: string;
  type: string;
  name: string;
  companies: string;
  account: string;
  msgBasic: string;
}

function BuilderRCSBasic() {
  useBgPages();
  const { t } = useTranslation();
  const { session, selectedAccount } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [accountOptions, setAccountOptions] = useState<any[]>([]);
  const { payload } = location.state || {};

  const defaultValues: FormValues = {
    _id: '',
    type: 'basic',
    name: '',
    companies: '',
    account: '',
    msgBasic: ''
  };

  const {
    register,
    watch,
    control,
    setValue,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const formValues = watch();

  const getCompanies = async () => {
    try {
      const data = await CompaniesService.getCompanies();
      setCompanies(
        data.map((item: any) => ({
          value: item._id,
          label: item.name,
          type: item.service_type
        }))
      );
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
      setCompanies([]);
    }
  };

  const getAccount = async () => {
    if (selectedCompany) {
      try {
        const data = await AccountsService.getAccountsForCompany(selectedCompany);
        setAccountOptions(
          data.map((item: any) => ({
            value: item._id,
            label: item.name,
            type: item.service_type
          }))
        );
      } catch (errorCode: any) {
        console.log('Error:', errorCode);
        AlertService.toastError(errorCode.error || errorCode);
        setAccountOptions([]);
      }
    }
  };

  const registerTemplate = async (data: any, buttonId: string) => {
    try {
      const addTemplate = {
        account: data.account,
        name: data.name,
        type: data.type.toUpperCase(),
        basic: {
          text: data.msgBasic
        }
      };

      const editTemplate = {
        _id: payload?.rows._id,
        type: payload?.rows.type.toUpperCase(),
        name: data.name,
        account: data.account,
        basic: {
          text: data.msgBasic
        }
      };

      const confirmed = await AlertService.question(
        t('general.atention'),
        `¿Deseas ${payload?.rows._id ? 'editar' : 'dar de alta'} la plantilla, ${
          payload?.rows._id ? payload?.rows.name : data.name
        }?`,
        null as any
      );

      if (!confirmed) return;

      const isEditing = Boolean(payload?.rows._id);
      const templateData = isEditing ? editTemplate : addTemplate;

      await (isEditing
        ? TemplatesService.editTemplateRCS(templateData)
        : TemplatesService.addTemplateRCS(templateData));

      AlertService.toastSuccess(
        `¡ Éxito !, tu plantilla se ${isEditing ? 'actualizó' : 'dio de alta'} correctamente.`
      );

      LogData(templateData, buttonId, window.location.href);
      reset(defaultValues);
      setValue('msgBasic', '');
      navigate('/templates-panel/templates-rcs');
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const countBytes = (text: string) => {
    const encoder = new TextEncoder();
    const encoded = encoder.encode(text);
    return encoded.length;
  };

  const isMessageTooLong = () => {
    const currentMessage = formValues.msgBasic || '';
    const byteCount = countBytes(currentMessage);
    return byteCount > 160;
  };

  useEffect(() => {
    getCompanies();
  }, []);

  useEffect(() => {
    if (session.profile === PROFILES.ADMIN) {
      setValue('account', selectedAccount.account_id);
    }
  }, [selectedAccount, session]);

  useEffect(() => {
    getAccount();
  }, [selectedCompany]);

  useEffect(() => {
    if (payload?.rows) {
      reset({
        _id: payload?.rows._id || defaultValues._id,
        type: defaultValues.type,
        name: payload?.rows.name || defaultValues.name,
        companies: payload?.rows.account.company._id || defaultValues.companies,
        account: payload?.rows.account._id || defaultValues.account,
        msgBasic: payload?.rows.basic?.text || defaultValues.msgBasic
      });
      setSelectedCompany(payload?.rows.account.company._id || defaultValues.companies);
    }
  }, [payload?.rows]);

  return (
    <>
      <div className="page d-flex flex-row flex-column-fluid">
        <Asaid />
        <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
          <Header />
          <div className="content fs-6 d-flex flex-column flex-column-fluid" id="kt_content">
            <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
              <div className="container-xxl">
                <Navigate
                  btnBack
                  title={payload?.rows._id ? 'Edita tu plantilla' : 'Crea tu plantilla'}
                  titleBolder={payload?.rows._id ? payload.rows.name : 'RCS Básico'}
                  subtitle={
                    payload?.rows._id
                      ? `${payload?.rows.account.company.name} - ${payload?.rows.account.name}`
                      : 'Ingresa los dato snecesarios para tu mensaje RCS básico.'
                  }
                  breadcrumbItems={['Herramientas', 'Plantillas', 'Editor']}
                />
                <form
                  onSubmit={handleSubmit((data) =>
                    registerTemplate(
                      data,
                      payload?.rows._id
                        ? 'editTemplateBasic-8349657902'
                        : 'addTemplateBasic-65320011778'
                    )
                  )}
                >
                  <div className="row g-5 g-xl-10 g-xl-10">
                    <div className="col-xl-8 mb-xl-10 mb-5">
                      <div className="card mb-5 mb-xxl-8">
                        <div className="card-body">
                          <Title
                            icon="ki-picture"
                            title="Contenido de plantilla"
                            subtitle="Ingresa el mensaje que necesites de tu plantilla."
                            type="info"
                          />
                          <div className="flex-column current">
                            <div className="pb-0">
                              <div className="row">
                                {session.profile === PROFILES.ROOT && !payload?.rows._id && (
                                  <div className="col-6">
                                    <div className="fv-row mb-7">
                                      <label className="form-label">
                                        {t('blacklist-page.company-label')}
                                        <span className="fs-8 text-muted ms-1 required"></span>
                                      </label>
                                      <Controller
                                        control={control}
                                        name="companies"
                                        rules={{ required: true }}
                                        render={({
                                          field: { onChange, value },
                                          fieldState: { error }
                                        }) => (
                                          <>
                                            <Select
                                              options={companies}
                                              noOptionsMessage={({ inputValue }) =>
                                                inputValue
                                                  ? t('general.no-options', { inputValue })
                                                  : t('general.no-options')
                                              }
                                              className={
                                                error
                                                  ? 'form-select-custom is-invalid'
                                                  : 'form-select-custom'
                                              }
                                              classNamePrefix="form-select-custom"
                                              placeholder={t('blacklist-page.company-placeholder')}
                                              onChange={(selectedOption) => {
                                                const selectedValue = selectedOption
                                                  ? selectedOption.value
                                                  : '';
                                                setSelectedCompany(selectedValue);
                                                onChange(selectedValue);
                                              }}
                                              value={
                                                companies.filter((option: any) =>
                                                  value?.includes(option.value)
                                                ) || []
                                              }
                                            />
                                            <InvalidFeedback error={error} />
                                          </>
                                        )}
                                      />
                                    </div>
                                  </div>
                                )}
                                {session.profile === PROFILES.ROOT && (
                                  <div className="col-6">
                                    <div className="fv-row mb-7">
                                      <label className="form-label required">
                                        {t('blacklist-page.account-label')}
                                      </label>
                                      <Controller
                                        control={control}
                                        name="account"
                                        rules={{ required: true }}
                                        render={({
                                          field: { onChange, value },
                                          fieldState: { error }
                                        }) => (
                                          <>
                                            <Select
                                              options={accountOptions}
                                              isClearable
                                              noOptionsMessage={({ inputValue }) =>
                                                inputValue
                                                  ? t('general.no-options', { inputValue })
                                                  : t('general.no-options')
                                              }
                                              className={
                                                error
                                                  ? 'form-select-custom is-invalid'
                                                  : 'form-select-custom'
                                              }
                                              classNamePrefix="form-select-custom"
                                              placeholder={t('blacklist-page.account-placeholder')}
                                              onChange={(selectedOption) => {
                                                onChange(selectedOption?.value);
                                              }}
                                              value={
                                                accountOptions.find(
                                                  (option: any) => option.value === value
                                                ) || null
                                              }
                                            />
                                            <InvalidFeedback error={error} />
                                          </>
                                        )}
                                      />
                                    </div>
                                  </div>
                                )}
                                <div className="col-6">
                                  <div className="fv-row mb-0">
                                    <Input
                                      label={t('blacklist-page.name')}
                                      requiredIndicator="required"
                                      placeholder={t('blacklist-page.name-placeholder')}
                                      errors={errors?.name}
                                      {...register('name', {
                                        required: true,
                                        setValueAs: (value) => value.trim()
                                      })}
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="separator separator-dashed separator-content text-dark my-10">
                                <img
                                  alt="Logo"
                                  src="/assets/media/logos/logo-c.svg"
                                  className="w-30px logo-minimize"
                                />
                              </div>
                              <div className="row mb-10">
                                <div className="fv-row">
                                  <TextAreaRCS
                                    label={t('templates.rcs.create.message-label')}
                                    requiredIndicator="required"
                                    inputName="msgBasic"
                                    payload={
                                      payload?.rows?.basic?.text
                                        ? { message: payload.rows.basic.text }
                                        : ''
                                    }
                                    placeholder={t('templates.rcs.create.message-placeholder')}
                                    errors={errors.msgBasic}
                                    counter={160}
                                    setValue={setValue}
                                    {...register('msgBasic', {
                                      required: true,
                                      maxLength: { value: 160, message: '160' }
                                    })}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="card-footer">
                          <div className="d-flex justify-content-end align-items-center">
                            <Button
                              type="submit"
                              typeStepperBtn="submit"
                              id="sendData"
                              className="btn btn-lg btn-primary hover-scale"
                              disabled={!isValid || isMessageTooLong()}
                              isLoading={isSubmitting}
                            >
                              {t('general.save')}
                              <i className="ki-duotone ki-send ms-2 fs-1">
                                <span className="path1"></span>
                                <span className="path2"></span>
                              </i>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-xl-4">
                      <div className="sticky-box">
                        <AndroidMockup type={'basic'} data={formValues} />
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
}

export default BuilderRCSBasic;
